import { useState } from 'react';
import { Sheet, field, hint, label } from '../contacts/Sheet.tsx';
import { useRecordAction, useTaskDepartments, type Department, type Task } from '../../api/taskQueries.ts';
import { localValue, timeState, whenState } from '../../tasks/fields.ts';
import { Refusal, TimeField, WhenField, go, plain, type DueValue } from './Fields.tsx';
import { FollowUpFields, NO_FOLLOW_UP, agencyToday, followUpToSend } from './FollowUp.tsx';
import { followUpSaid } from '../../tasks/taskView.ts';

/**
 * "Record an action" (ruling 16): a task opened and completed in one step, source "Action", done directly (the server
 * marks it; nothing infers it from equal times). Opened from a department section of the client page (A-#144: the
 * client and the department are the page's, never chosen here), or from a task card, where it completes THAT task
 * instead (its client and department are the task's, and "what was done" is the task's own title).
 *
 * The server checks the caller may record for that department and client (147's task_record_action), whatever is
 * sent. A follow-up is offered only in a department of the `follow_up` list (GET /tasks/departments).
 */
export function RecordActionForm({ fromTask, client: given, department: section, onDone, onCancel }: {
  fromTask?: Task;
  client?: { id: string; name: string; ref: string | null } | null;
  /** The section's department (A-#144); with a task, the task's. */
  department?: Department;
  onDone: (said: string | null) => void;
  onCancel: () => void;
}) {
  const departments = useTaskDepartments();
  const action = useRecordAction();
  const today = agencyToday();
  const client = fromTask?.client ?? given ?? null;
  const department: Department | '' = fromTask?.department ?? section ?? '';
  const mayFollowUp = department !== '' && (departments.data?.follow_up ?? []).includes(department);
  const [what, setWhat] = useState('');
  const [note, setNote] = useState('');
  const [time, setTime] = useState('');
  const [when, setWhen] = useState(() => localValue(new Date()));
  const [follow, setFollow] = useState(NO_FOLLOW_UP as { on: boolean; title: string; due: DueValue; to: string });
  const spent = timeState(time);
  const at = whenState(when, new Date());
  const fu = mayFollowUp ? followUpToSend(follow, today) : undefined;
  const ready = client !== null && department !== '' && (fromTask ? true : what.trim() !== '') && note.trim() !== ''
    && 'minutes' in spent && 'at' in at && fu !== null;

  return (
    <form className="space-y-3" onSubmit={(e) => {
      e.preventDefault();
      if (!ready) return;
      action.mutate({
        client_id: client!.id,
        department: department as Department,
        what: fromTask ? fromTask.title : what.trim(),
        note: note.trim(),
        spent: { minutes: (spent as { minutes: number }).minutes },
        happened_at: (at as { at: string }).at,
        ...(fromTask ? { task_id: fromTask.id } : {}),
        ...(fu ? { follow_up: fu } : {}),
      }, { onSuccess: (r) => onDone(followUpSaid(r.follow_up)) });
    }}>
      {fromTask && <p className="text-neutral-900">This completes the task: {fromTask.title}.</p>}
      {client && (
        <p className="text-neutral-900"><span className={label}>Client</span>{client.name} <span className="font-mono text-sm text-neutral-700">{client.ref}</span></p>
      )}
      {department !== '' && <p className="text-neutral-900"><span className={label}>Department</span>{department}</p>}
      {!fromTask && (
        <label className={label}>What was done
          <input type="text" maxLength={200} value={what} onChange={(e) => setWhat(e.target.value)} className={field} />
        </label>
      )}
      <label className={label}>Note
        <textarea rows={4} maxLength={4000} value={note} onChange={(e) => setNote(e.target.value)} className={`${field} py-2`} />
      </label>
      <TimeField value={time} onChange={setTime} />
      <WhenField value={when} onChange={setWhen} />
      {mayFollowUp && <FollowUpFields department={department as Department} today={today} value={follow} onChange={setFollow} />}
      {!ready && <p className={hint}>{fromTask ? 'A note' : 'What was done, a note'}, the time spent and when it happened are needed.</p>}
      <Refusal error={action.error ?? departments.error} />
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={go} disabled={!ready || action.isPending}>Record the action</button>
        <button type="button" className={plain} onClick={onCancel}>{fromTask ? 'Back' : 'Cancel'}</button>
      </div>
    </form>
  );
}

/** The sheet a department section of the client page opens: its client and its department. */
export function RecordActionSheet({ client, department, onClose }: {
  client: { id: string; name: string; ref: string | null };
  department: Department;
  onClose: () => void;
}) {
  const [done, setDone] = useState<{ said: string | null } | null>(null);
  return (
    <Sheet labelId="record-action" title="Record an action" onClose={onClose}>
      {done ? (
        <div className="space-y-3">
          <p role="status" className="font-semibold text-neutral-900">The action is recorded.{done.said ? ` ${done.said}` : ''}</p>
          <button type="button" className={go} onClick={onClose}>Close</button>
        </div>
      ) : (
        <RecordActionForm client={client} department={department} onDone={(said) => setDone({ said })} onCancel={onClose} />
      )}
    </Sheet>
  );
}
